import { ReactNode, useEffect, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'

import { useOrgDetails } from '@/lib/auth/orgid.hook'

import '@/components/shared/export-button/print.less'
import { WebsiteLogo } from '@/components/shared/website-logo/website-logo'

type UseExportOptions = {
  documentTitle?: string
}

type UseExportReturn<T extends HTMLElement> = {
  exportRef: React.RefObject<T>
  isExporting: boolean
  handleExport: () => void
  ExportContent: React.FC
}

export function useExport<T extends HTMLDivElement>(
  getExportContent: () => {
    exportContent: ReactNode
    renderContent: () => void
    isLoaded: boolean
  },
  options?: UseExportOptions,
): UseExportReturn<T> {
  const { orgDetails } = useOrgDetails()
  const [isBeforeGetContent, setIsBeforeGetContent] = useState(false)
  const [contentReady, setContentReady] = useState(false)
  const [exportData, setExportData] = useState<{
    exportContent: ReactNode
    renderContent: () => void
    isLoaded: boolean
  } | null>(null)
  const exportRef = useRef<T>(null)

  const { isLoaded } = exportData || {
    isLoaded: false,
  }

  const reactToPrintFn = useReactToPrint({
    contentRef: exportRef,
    documentTitle: options?.documentTitle,
    onAfterPrint: () => {
      setIsBeforeGetContent(false)
      setExportData(null)
    },
    preserveAfterPrint: true,
  })

  useEffect(() => {
    if (isBeforeGetContent && isLoaded && exportData) {
      // oxlint-disable-next-line react/set-state-in-effect
      setContentReady(true)
    }
  }, [isBeforeGetContent, isLoaded, exportData])

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    if (isBeforeGetContent && contentReady && isLoaded && exportData) {
      timer = setTimeout(() => {
        reactToPrintFn()
      }, 250)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isBeforeGetContent, contentReady, isLoaded, exportData, reactToPrintFn])

  const handleExport = () => {
    const data = getExportContent()
    setExportData(data)
    data.renderContent()
    setIsBeforeGetContent(true)
  }

  useEffect(() => {
    if (isBeforeGetContent && exportData) {
      const recheckData = getExportContent()

      if (exportData.isLoaded !== recheckData.isLoaded) {
        // oxlint-disable-next-line react/set-state-in-effect
        setExportData(recheckData)
      }
    }
  }, [isBeforeGetContent, exportData, getExportContent])

  const ExportContent: React.FC = () => {
    if (!isBeforeGetContent || !contentReady) {
      return null
    }

    return (
      <div className='invisible absolute'>
        <div ref={exportRef} className='print-container'>
          <div className='print-header'>
            <div className='p-2'>
              <WebsiteLogo orgId={orgDetails?.orgId} />
            </div>
          </div>
          <div className='print-content'>{exportData?.exportContent}</div>
        </div>
      </div>
    )
  }

  return {
    exportRef: exportRef as React.RefObject<T>,
    isExporting: isBeforeGetContent,
    handleExport,
    ExportContent,
  }
}
